import { Dispatch } from 'redux'

import type { TCreateRerunRequestBody } from '@homepal/api-contract'

import {
  cancelRerun,
  createRerun,
  getRerun,
  getRerunCandidates,
  getReruns,
  retryFailedRerun
} from 'redux/api/internal/DataReruns'

import { ApplicationState } from 'redux/Stores/types'
import { EReduxState } from 'redux/utils/State'

import { toggleSnackbarOpen } from 'redux/actions/Snackbar'
import { useThunkActions } from 'redux/actions/useThunkActions'
import { SnackbarMessage } from 'types/GlobalSnackbar'

import * as actions from './actions'

function tryGetRerunCandidates(customerId?: string) {
  return (dispatch: Dispatch) => {
    dispatch(actions.getCandidates({ customerId }))

    getRerunCandidates(customerId)
      .then((response) => {
        dispatch(
          actions.getCandidatesSuccess({ data: response.data.data, customerId })
        )
      })
      .catch(() => {
        dispatch(actions.getCandidatesFailed({ customerId }))
      })
  }
}

function tryCreateRerun(
  body: TCreateRerunRequestBody,
  callback?: (err?: boolean, rerunId?: string) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.create())

    createRerun(body)
      .then((response) => {
        dispatch(actions.createSuccess())
        dispatch(toggleSnackbarOpen(SnackbarMessage.Success))

        if (callback) {
          callback(false, response.data.data.id)
        }
      })
      .catch(() => {
        dispatch(actions.createFailed())
        dispatch(toggleSnackbarOpen(SnackbarMessage.Failed))

        if (callback) {
          callback(true)
        }
      })
  }
}

/** Skips while a request is in flight, else fetches even if loaded. */
function tryGetReruns() {
  return (dispatch: Dispatch, getState: () => ApplicationState) => {
    if (
      getState().InternalDataRerunsStore.rerunsFetchStatus ===
      EReduxState.LOADING
    ) {
      return
    }

    tryRefetchReruns()(dispatch)
  }
}

function tryRefetchReruns() {
  return (dispatch: Dispatch) => {
    dispatch(actions.getReruns())

    getReruns(false)
      .then((response) => {
        dispatch(actions.getRerunsSuccess(response.data.data))
      })
      .catch(() => {
        dispatch(actions.getRerunsFailed())
      })
  }
}

/** Skips while a request is in flight, else fetches even if loaded. */
function tryGetActiveReruns() {
  return (dispatch: Dispatch, getState: () => ApplicationState) => {
    if (
      getState().InternalDataRerunsStore.activeRerunsFetchStatus ===
      EReduxState.LOADING
    ) {
      return
    }

    tryRefetchActiveReruns()(dispatch)
  }
}

function tryRefetchActiveReruns() {
  return (dispatch: Dispatch) => {
    dispatch(actions.getActiveReruns())

    getReruns(true)
      .then((response) => {
        dispatch(actions.getActiveRerunsSuccess(response.data.data))
      })
      .catch(() => {
        dispatch(actions.getActiveRerunsFailed())
      })
  }
}

/** Skips while the same rerun is in flight, else fetches even if loaded. */
function tryGetRerun(id: string) {
  return (dispatch: Dispatch, getState: () => ApplicationState) => {
    const { requestedRerunId, selectedRerunFetchStatus } =
      getState().InternalDataRerunsStore

    if (
      requestedRerunId === id &&
      selectedRerunFetchStatus === EReduxState.LOADING
    ) {
      return
    }

    tryRefetchRerun(id)(dispatch)
  }
}

function tryRefetchRerun(id: string) {
  return (dispatch: Dispatch) => {
    dispatch(actions.getRerun({ id }))

    getRerun(id)
      .then((response) => {
        dispatch(actions.getRerunSuccess(response.data.data))
      })
      .catch(() => {
        dispatch(actions.getRerunFailed({ id }))
      })
  }
}

function tryCancelRerun(id: string, callback?: (err?: boolean) => void) {
  return (dispatch: Dispatch) => {
    dispatch(actions.cancel())

    cancelRerun(id)
      .then(() => {
        dispatch(actions.cancelSuccess())
        dispatch(toggleSnackbarOpen(SnackbarMessage.Success))

        if (callback) {
          callback(false)
        }
      })
      .catch(() => {
        dispatch(actions.cancelFailed())
        dispatch(toggleSnackbarOpen(SnackbarMessage.Failed))

        if (callback) {
          callback(true)
        }
      })
  }
}

function tryRetryFailedRerun(
  id: string,
  callback?: (err?: boolean, rerunId?: string) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.retryFailed())

    retryFailedRerun(id)
      .then((response) => {
        dispatch(actions.retryFailedSuccess())
        dispatch(toggleSnackbarOpen(SnackbarMessage.Success))

        if (callback) {
          callback(false, response.data.data.id)
        }
      })
      .catch(() => {
        dispatch(actions.retryFailedFailed())
        dispatch(toggleSnackbarOpen(SnackbarMessage.Failed))

        if (callback) {
          callback(true)
        }
      })
  }
}

export const useDataRerunsActions = () => {
  return {
    tryCancelRerun: useThunkActions(tryCancelRerun),
    tryCreateRerun: useThunkActions(tryCreateRerun),
    tryGetActiveReruns: useThunkActions(tryGetActiveReruns),
    tryGetRerun: useThunkActions(tryGetRerun),
    tryGetRerunCandidates: useThunkActions(tryGetRerunCandidates),
    tryGetReruns: useThunkActions(tryGetReruns),
    tryRefetchActiveReruns: useThunkActions(tryRefetchActiveReruns),
    tryRefetchRerun: useThunkActions(tryRefetchRerun),
    tryRefetchReruns: useThunkActions(tryRefetchReruns),
    tryRetryFailedRerun: useThunkActions(tryRetryFailedRerun)
  }
}
